import React, { useEffect, useState } from "react";
import Gauge from "./Gauge";
import { Numeral } from "react-numeral";
import RadarChart from "../charts/Radar";
import Image from "../../Tags/Image";

export default function Glassdoor({ data = {} }) {
  const [currentValue, setCurrentValue] = useState(null);
  const [currentType, setCurrentType] = useState("");
  const [animationClass, setAnimationClass] = useState("");

  // Initialize data and set the first available value
  useEffect(() => {
    const nextValues = {
      month: data?.review_count_mom_pct,
      quarter: data?.review_count_qoq_pct,
      year: data?.review_count_yoy_pct,
    };

    // Filter out the types with null or undefined values
    const validTypes = Object.keys(nextValues).filter((type) => nextValues[type] != null);

    if (validTypes.length > 0) {
      // Set initial value and type
      setCurrentType(validTypes[0]);
      setCurrentValue(nextValues[validTypes[0]]);
    }
  }, [data]);

  useEffect(() => {
    if (!currentType || !currentValue) return;

    const nextValues = {
      month: data?.review_count_mom_pct,
      quarter: data?.review_count_qoq_pct,
      year: data?.review_count_yoy_pct,
    };

    const validTypes = Object.keys(nextValues).filter((type) => nextValues[type] != null);
    setAnimationClass("animate__animated animate__fadeInUp");

    const updateValue = () => {
      if (validTypes.length === 0) return; // Exit if no valid data available

      let nextTypeIndex = validTypes.indexOf(currentType) + 1;
      if (nextTypeIndex >= validTypes.length) {
        nextTypeIndex = 0; // Loop back to the first valid type
      }

      const newType = validTypes[nextTypeIndex];
      const newValue = nextValues[newType];

      console.log("new", { newValue, currentValue });

      if (newType !== currentType) {
        // Update the value and type
        setCurrentValue(newValue);
        setCurrentType(newType);
      }
    };

    const intervalId = setInterval(updateValue, 3000); // Update every 3 seconds

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [currentType, currentValue]);

  useEffect(() => {
    if (animationClass) {
      const animationDuration = 1000; // Animation duration in ms
      const timeoutId = setTimeout(() => {
        setAnimationClass("");
      }, animationDuration);

      return () => clearTimeout(timeoutId); // Cleanup timeout on re-render
    }
  }, [animationClass]);

  const ratings = {
    senior_management_rating: data?.senior_management_rating,
    compensation_rating: data?.compensation_rating,
    career_opportunities_rating: data?.career_opportunities_rating,
  };

  return (
    <div className="border rounded-[14px] overflow-hidden border-border  bg-background relative mt-4  py-2 px-4">
      <h3 className="text-gray-900 my-2 mb-4 inline-flex space-x-2">
        <Image className="no-invert" src="/images/ext/companyinfo/glassdoor.svg" />
        <span>Glassdoor</span>
      </h3>
      <div className="flex mb-2 space-x-4  mobile:block mobile:mx-0 mobile:space-x-0 mobile:px-0 mobile:space-y-4">
        <div className="p-6 border border-gray-200 bg-gray-50 rounded-xl flex-1 ">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z"
              fill="var(--success-100)"
            />
            <path
              d="M23.2827 15.4533C23.5131 14.9864 23.6284 14.7529 23.7848 14.6783C23.9209 14.6134 24.0791 14.6134 24.2152 14.6783C24.3717 14.7529 24.4869 14.9864 24.7174 15.4533L26.9041 19.8833C26.9721 20.0211 27.0061 20.0901 27.0558 20.1436C27.0999 20.191 27.1527 20.2293 27.2113 20.2566C27.2776 20.2874 27.3536 20.2985 27.5057 20.3208L32.397 21.0357C32.9121 21.111 33.1696 21.1486 33.2888 21.2744C33.3925 21.3839 33.4412 21.5343 33.4215 21.6838C33.3988 21.8556 33.2124 22.0372 32.8395 22.4004L29.3014 25.8464C29.1912 25.9538 29.136 26.0076 29.1004 26.0715C29.0689 26.128 29.0487 26.1902 29.0409 26.2545C29.0321 26.3271 29.0451 26.403 29.0711 26.5547L29.906 31.4221C29.994 31.9355 30.038 32.1922 29.9553 32.3445C29.8833 32.477 29.7554 32.57 29.6071 32.5975C29.4366 32.6291 29.2061 32.5078 28.7451 32.2654L24.3724 29.9658C24.2361 29.8942 24.168 29.8584 24.0962 29.8443C24.0327 29.8318 23.9673 29.8318 23.9038 29.8443C23.832 29.8584 23.7639 29.8942 23.6277 29.9658L19.2549 32.2654C18.7939 32.5078 18.5634 32.6291 18.393 32.5975C18.2447 32.57 18.1167 32.477 18.0447 32.3445C17.962 32.1922 18.006 31.9355 18.0941 31.4221L18.9289 26.5547C18.9549 26.403 18.9679 26.3271 18.9591 26.2545C18.9513 26.1902 18.9311 26.128 18.8996 26.0715C18.864 26.0076 18.8089 25.9538 18.6986 25.8464L15.1606 22.4004C14.7877 22.0372 14.6012 21.8556 14.5785 21.6838C14.5588 21.5343 14.6076 21.3839 14.7113 21.2744C14.8304 21.1486 15.088 21.111 15.603 21.0357L20.4943 20.3208C20.6464 20.2985 20.7225 20.2874 20.7887 20.2566C20.8474 20.2293 20.9002 20.191 20.9442 20.1436C20.9939 20.0901 21.0279 20.0211 21.096 19.8833L23.2827 15.4533Z"
              stroke="var(--success-600)"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <p className="text-sm text-gray-600 mt-6 mb-3">Overall Rating</p>
          <p className="text-gray-900 text-4xl font-bold">
            <Numeral value={data?.overall_rating} format={"0.[0]a"} />
          </p>
        </div>
        <div className="border rounded-[14px] overflow-hidden border-gray-200 bg-gray-50 relative p-4 flex-1">
          <span className="text-[12px] capitalize whitespace-pre mb-4 leading-none block text-sm">
            Review count
          </span>
          <div className="flex justify-between items-center">
            <Numeral
              className="text-gray-900 font-bold leading-none uppercase text-3xl"
              value={data?.total_reviews}
              format={"0.[0]a"}
            />
            {currentValue && (
              <span className={`inline-flex space-x-1 ml-3 items-center ${animationClass}`}>
                <Image className="no-invert"
                  src={
                    currentValue < 0
                      ? "/images/ext/companyinfo/downtrend.svg"
                      : "/images/ext/companyinfo/uptrend.svg"
                  }
                />
                <span className={`${currentValue < 0 ? "text-error-600" : "text-success-500"} text-sm`}>
                  {currentValue}%
                </span>
                <span>vs last {currentType}</span>
              </span>
            )}
          </div>
          <div className="mt-6">
            {currentValue ? (
              <Image
                className="w-full no-invert"
                src={
                  currentValue < 0
                    ? "/images/ext/companyinfo/downtrend-chart.svg"
                    : "/images/ext/companyinfo/uptrend-chart.svg"
                }
              />
            ) : (
              <Image className="w-full no-invert" src={"/images/ext/companyinfo/notrend-chart.svg"} />
            )}
          </div>
        </div>
      </div>
      {data &&
        data?.senior_management_rating &&
        data?.compensation_rating &&
        data?.career_opportunities_rating && (
          <div className="p-6 border border-gray-200 bg-gray-50 rounded-xl  mt-4 mb-2">
            <h3 className="text-center mb-4 mt-2">Ratings by category</h3>
            <RadarChart data={ratings} />
          </div>
        )}
    </div>
  );
}
